import React, { useEffect, useState, useMemo } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import LiveCommunications from "./components/Dashboard/LiveCommunications";
import AdvancedAudioPlayer from './components/Dashboard/AdvancedAudioPlayer';
import SettingsPage from "./components/Settings/SettingsPage";
import UserManagement from "./components/Users/index";
import LogsPage from "./components/Logs/LogsPage";
import ReportPage from "./components/ReportPage";
import axios from "axios";
import "./App.css";
import { AuthProvider } from './components/AuthContext';
import { PrivateRoute } from './components/PrivateRoute';
import LoginPage from './components/LoginPage';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CACHE_KEYS = {
  CHANNELS: 'cached_channels',
  MESSAGES: 'cached_messages',
  KEYWORDS: 'cached_keywords',
  TIMEZONE: 'cached_timezone',
  TIME_FORMAT: 'cached_time_format',
  LAST_FETCH: 'last_fetch_time'
};

// API endpoints configuration
const API_ENDPOINTS = {
  MESSAGES: '/recordings', // Change to '/records' if your API uses that endpoint
  CHANNELS: '/channels',
  SETTINGS: '/settings'
};

const CACHE_DURATION = 50 * 60 * 1000; // 50 minutes in milliseconds

// Cache management utilities
const getCacheSize = (key) => {
  try {
    const data = localStorage.getItem(key);
    return data ? new Blob([data]).size : 0;
  } catch (error) {
    return 0;
  }
};

const getTotalCacheSize = () => {
  try {
    let totalSize = 0;
    Object.values(CACHE_KEYS).forEach(key => {
      totalSize += getCacheSize(key);
    });
    return totalSize;
  } catch (error) {
    return 0;
  }
};

const clearOldCache = () => {
  try {
    const lastFetch = getCachedData(CACHE_KEYS.LAST_FETCH);
    if (lastFetch && (Date.now() - lastFetch > CACHE_DURATION * 2)) {
      // Clear cache if it's older than 2x the cache duration
      Object.values(CACHE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
      console.log('Cleared old cache data');
    }
  } catch (error) {
    console.error('Error clearing old cache:', error);
  }
};

const validateTimezone = (tz) => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch (e) {
    return false;
  }
};

// Move cache functions here, before they're used
const cacheData = (key, data) => {
  try {
    // For messages, ensure they're properly ordered before caching
    if (key === CACHE_KEYS.MESSAGES && Array.isArray(data)) {
      data = sortMessagesByTime(data);
      console.log(`Messages sorted and ready for caching (latest to oldest): ${data.length} messages`);
    }
    
    // Check if data is too large for localStorage
    const dataString = JSON.stringify(data);
    const dataSize = new Blob([dataString]).size;
    const maxSize = 4 * 1024 * 1024; // 4MB limit to be safe
    
    if (dataSize > maxSize) {
      console.warn(`Data for ${key} is too large (${(dataSize / 1024 / 1024).toFixed(2)}MB), implementing size reduction`);
      
      // For messages, keep only the most recent ones (first in array since they're sorted latest to oldest)
      if (key === CACHE_KEYS.MESSAGES && Array.isArray(data)) {
        const maxMessages = 1000; // Keep only first 1000 messages (most recent)
        const reducedData = data.slice(0, maxMessages);
        const reducedString = JSON.stringify(reducedData);
        const reducedSize = new Blob([reducedString]).size;
        
        if (reducedSize <= maxSize) {
          localStorage.setItem(key, reducedString);
          console.log(`Successfully cached ${reducedData.length} most recent messages (reduced from ${data.length})`);
        } else {
          // If still too large, keep even fewer messages
          const furtherReduced = data.slice(0, 500);
          localStorage.setItem(key, JSON.stringify(furtherReduced));
          console.log(`Successfully cached ${furtherReduced.length} most recent messages (further reduced)`);
        }
      } else {
        // For other data types, try to store a subset or skip caching
        console.warn(`Skipping cache for ${key} due to size constraints`);
        return false;
      }
    } else {
      localStorage.setItem(key, dataString);
    }
    return true;
  } catch (error) {
    if (error.name === 'QuotaExceededError') {
      console.warn(`Storage quota exceeded for ${key}, attempting cleanup`);
      
      // Try to clear some old cache entries
      try {
        const keysToClear = [CACHE_KEYS.MESSAGES, CACHE_KEYS.CHANNELS, CACHE_KEYS.KEYWORDS];
        keysToClear.forEach(cacheKey => {
          if (cacheKey !== key) {
            localStorage.removeItem(cacheKey);
          }
        });
        
        // Try caching again with reduced data
        if (key === CACHE_KEYS.MESSAGES && Array.isArray(data)) {
          const reducedData = data.slice(0, 500); // Keep first 500 (most recent)
          localStorage.setItem(key, JSON.stringify(reducedData));
          console.log(`Successfully cached ${reducedData.length} most recent messages after cleanup`);
          return true;
        }
      } catch (cleanupError) {
        console.error(`Failed to cleanup and retry caching for ${key}:`, cleanupError);
      }
    }
    console.error(`Error caching ${key}:`, error);
    return false;
  }
};

const getCachedData = (key) => {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error(`Error retrieving ${key} from cache:`, error);
    return null;
  }
};

// Function to log cache usage for debugging
const logCacheUsage = () => {
  try {
    const totalSize = getTotalCacheSize();
    console.log(`Total cache size: ${(totalSize / 1024 / 1024).toFixed(2)}MB`);
    
    Object.entries(CACHE_KEYS).forEach(([name, key]) => {
      const size = getCacheSize(key);
      if (size > 0) {
        console.log(`${name}: ${(size / 1024 / 1024).toFixed(2)}MB`);
      }
    });
  } catch (error) {
    console.error('Error logging cache usage:', error);
  }
};

// Function to get cache statistics
const getCacheStats = () => {
  try {
    const stats = {};
    Object.entries(CACHE_KEYS).forEach(([name, key]) => {
      const data = getCachedData(key);
      if (data) {
        if (Array.isArray(data)) {
          stats[name] = { count: data.length, size: getCacheSize(key) };
        } else if (typeof data === 'object') {
          stats[name] = { count: Object.keys(data).length, size: getCacheSize(key) };
        } else {
          stats[name] = { count: 1, size: getCacheSize(key) };
        }
      }
    });
    return stats;
  } catch (error) {
    console.error('Error getting cache stats:', error);
    return {};
  }
};

// Function to check if cache is healthy
const isCacheHealthy = () => {
  try {
    const stats = getCacheStats();
    const totalSize = getTotalCacheSize();
    const maxSize = 10 * 1024 * 1024; // 10MB limit
    
    return {
      healthy: totalSize < maxSize,
      totalSize,
      maxSize,
      stats
    };
  } catch (error) {
    console.error('Error checking cache health:', error);
    return { healthy: false, error: error.message };
  }
};

// Function to sort messages by time (latest to oldest)
const sortMessagesByTime = (messages) => {
  if (!Array.isArray(messages)) return messages;
  
  return messages.sort((a, b) => {
    const timeA = new Date(a.time || a.timestamp || 0).getTime();
    const timeB = new Date(b.time || b.timestamp || 0).getTime();
    return timeB - timeA; // Most recent first (latest to oldest)
  });
};

// Function to process and order messages from API response
const processMessagesFromAPI = (apiData, timezone, edgeServerEndpoint) => {
  if (!Array.isArray(apiData)) return [];
  
  const processedMessages = apiData.map(item => ({
    channel: item.channel_id.toString(),
    team: `Channel ${item.channel_id}`,
    time: item.timestamp,
    timezone: timezone,
    status: item.hasOwnProperty("status") ? item.status : "new",
    id: item.id,
    url: `${edgeServerEndpoint}/${item.filename.replace(/\\/g, '/')}`,
    message: item.transcription || "No transcription available",
    isNew: true,
  }));
  
  // Sort messages by time (latest to oldest)
  return sortMessagesByTime(processedMessages);
};

const App = () => {
  const [channels, setChannels] = useState({});
  const [messages, setMessages] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [endpointInput, setEndpointInput] = useState("");
  const [validationStatus, setValidationStatus] = useState("pending");
  const [edgeServerEndpoint, setEdgeServerEndpoint] = useState(
    localStorage.getItem("EDGE_SERVER_ENDPOINT") || process.env.REACT_APP_EDGE_SERVER_ENDPOINT || ""
  );
  
  // Add cache debugging to window object for development
  if (process.env.NODE_ENV === 'development') {
    window.debugCache = {
      getStats: getCacheStats,
      isHealthy: isCacheHealthy,
      clearCache: () => {
        Object.values(CACHE_KEYS).forEach(key => localStorage.removeItem(key));
        console.log('Cache cleared');
      },
      logUsage: logCacheUsage,
      getLatestMessages: (count = 10) => {
        const messages = getCachedData(CACHE_KEYS.MESSAGES) || [];
        return messages.slice(0, count);
      },
      getMessageCount: () => {
        const messages = getCachedData(CACHE_KEYS.MESSAGES) || [];
        return messages.length;
      },
      sortMessages: sortMessagesByTime
    };
  }
  
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return JSON.parse(localStorage.getItem("isDarkMode")) || false;
  });
  
  const [timezone, setTimezone] = useState(() => {
    const cachedTimezone = getCachedData(CACHE_KEYS.TIMEZONE);
    return cachedTimezone && validateTimezone(cachedTimezone) ? cachedTimezone : "Etc/UTC";
  });

  const [timeFormat, setTimeFormat] = useState(() => {
    const cachedTimeFormat = getCachedData(CACHE_KEYS.TIME_FORMAT);
    return cachedTimeFormat || "24h"; // Default to 24-hour format
  });

  const [branding, setBranding] = useState({
    organization_name: 'Potawatomi Tribal Fire Department'
  });

  const [reverseSort, setReverseSort] = useState(false);

  const toggleTheme = () => setIsDarkMode(prev => !prev);

  const setTimezoneWithLogging = (newTz) => {
    console.log(`Timezone changed from ${timezone} to ${newTz}`);
    setTimezone(newTz);
  };

  const setTimeFormatWithLogging = (newFormat) => {
    console.log(`Time format changed from ${timeFormat} to ${newFormat}`);
    setTimeFormat(newFormat);
    cacheData(CACHE_KEYS.TIME_FORMAT, newFormat);
  };

  // Fetch branding data and update document title
  const fetchBrandingData = async () => {
    try {
      const response = await axios.get(`${edgeServerEndpoint}/branding`);
      const brandingData = response.data;
      setBranding(brandingData);
      
      // Update document title with organization name
      if (brandingData.organization_name) {
        document.title = brandingData.organization_name;
      } else {
        // Fallback to default title
        document.title = 'Potawatomi Tribal Fire Department';
      }
    } catch (error) {
      console.error('Error fetching branding data:', error);
      // Keep default title if fetch fails
      document.title = 'Potawatomi Tribal Fire Department';
    }
  };

  useEffect(() => {
    localStorage.setItem("isDarkMode", JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  // Update document title when branding changes
  useEffect(() => {
    if (branding.organization_name) {
      document.title = branding.organization_name;
    }
  }, [branding.organization_name]);

  // Memoized processed messages
  const processedMessages = useMemo(() => {
    return messages.map(message => ({
      ...message,
      localTime: new Date(message.time).toLocaleString('en-US', { 
        timeZone: timezone,
        hour12: timeFormat === "12h"
      })
    }));
  }, [messages, timezone, timeFormat]);

  const loadCachedData = () => {
    const cachedChannels = getCachedData(CACHE_KEYS.CHANNELS);
    const cachedMessages = getCachedData(CACHE_KEYS.MESSAGES);
    const cachedKeywords = getCachedData(CACHE_KEYS.KEYWORDS);
    const cachedTimezone = getCachedData(CACHE_KEYS.TIMEZONE);
    const cachedTimeFormat = getCachedData(CACHE_KEYS.TIME_FORMAT);

    if (cachedChannels) {
      console.log(`Loaded ${Object.keys(cachedChannels).length} channels from cache`);
      setChannels(cachedChannels);
    }
    if (cachedMessages) {
      // Ensure cached messages are properly ordered (latest to oldest)
      const orderedMessages = sortMessagesByTime(cachedMessages);
      console.log(`Loaded ${orderedMessages.length} messages from cache (ordered latest to oldest)`);
      setMessages(orderedMessages);
    }
    if (cachedKeywords) {
      console.log(`Loaded ${cachedKeywords.length} keywords from cache`);
      setKeywords(cachedKeywords);
    }
    if (cachedTimezone && validateTimezone(cachedTimezone)) {
      setTimezoneWithLogging(cachedTimezone);
    }
    if (cachedTimeFormat) {
      setTimeFormatWithLogging(cachedTimeFormat);
    }
  };

  const isCacheValid = () => {
    const lastFetch = getCachedData(CACHE_KEYS.LAST_FETCH);
    return lastFetch && (Date.now() - lastFetch < CACHE_DURATION);
  };

  // Endpoint validation
  const validateEndpoint = async (url) => {
    try {
      const response = await axios.get(`${url}/ping`, { timeout: 5000 });
      return response.status === 200;
    } catch (error) {
      console.error("Endpoint validation error:", error);
      return false;
    }
  };

  const handleEndpointSubmit = async () => {
    setValidationStatus("validating");
    setError(null);
    
    try {
      new URL(endpointInput);
      const isValid = await validateEndpoint(endpointInput);
      
      if (isValid) {
        setEdgeServerEndpoint(endpointInput);
        localStorage.setItem("EDGE_SERVER_ENDPOINT", endpointInput);
        setShowModal(false);
        setValidationStatus("pending");
      } else {
        throw new Error("Could not connect to server");
      }
    } catch (error) {
      setValidationStatus("failed");
      setError(error.message);
    }
  };

  // Initial load
  useEffect(() => {
    const initialize = async () => {
      if (!edgeServerEndpoint) {
        setShowModal(true);
        return;
      }

      // Load cached data first for immediate rendering
      loadCachedData();
      
      setLoading(true);

      try {
        // Always fetch fresh data in background, regardless of cache validity
        // This ensures we have the most up-to-date information
        await fetchAllData();
        // Always fetch branding data to update title
        await fetchBrandingData();
      } catch (error) {
        console.error("Initialization error:", error);
        // Only show error if we don't have any cached data to fall back on
        if (!getCachedData(CACHE_KEYS.MESSAGES)) {
          setError("Failed to initialize application");
        } else {
          console.warn("Using cached data due to fetch error:", error);
        }
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [edgeServerEndpoint]);

  // Data fetching
  const fetchAllData = async () => {
    try {
      setLoading(true);
      // Fetch data from API endpoints
      const [channelsRes, messagesRes, keywordsRes] = await Promise.all([
        axios.get(`${edgeServerEndpoint}${API_ENDPOINTS.CHANNELS}`),
        axios.get(`${edgeServerEndpoint}${API_ENDPOINTS.MESSAGES}`), // This fetches the messages/recordings
        axios.get(`${edgeServerEndpoint}${API_ENDPOINTS.SETTINGS}`)
      ]);

      // Process channels
      const channelsData = channelsRes.data.reduce((acc, channel) => {
        if (channel.status !== 'disabled') {
          acc[channel.id] = {
            name: channel.name,
            status: channel.status,
            id: channel.id,
            driver: channel.driver,
            person: channel.person,
            car: channel.car,
            color: channel.color,
            background_color: channel.background_color,
            team_color: channel.team_color,
            silence: channel.silence,
            mac: channel.mac,
            sensitivity: channel.sensitivity,
            min_rec: channel.min_rec,
            max_rec: channel.max_rec,
            audio_gain: channel.audio_gain,
            tag: channel.tag,
            isActive: true,
          };
        }
        return acc;
      }, {});

      // Process messages and ensure they're ordered latest to oldest
      const messagesData = processMessagesFromAPI(messagesRes.data, timezone, edgeServerEndpoint);

      // Update state
      setChannels(channelsData);
      setMessages(messagesData);
      setKeywords(keywordsRes.data.keywords);

      // Handle timezone
      const newTimezone = keywordsRes.data.global_timezone || "Etc/UTC";
      if (validateTimezone(newTimezone)) {
        setTimezoneWithLogging(newTimezone);
        cacheData(CACHE_KEYS.TIMEZONE, newTimezone);
      } else {
        console.warn(`Invalid timezone received: ${newTimezone}, falling back to UTC`);
        setTimezoneWithLogging("Etc/UTC");
        cacheData(CACHE_KEYS.TIMEZONE, "Etc/UTC");
      }

      // Update cache with fresh data
      const cacheResults = {
        channels: cacheData(CACHE_KEYS.CHANNELS, channelsData),
        messages: cacheData(CACHE_KEYS.MESSAGES, messagesData),
        keywords: cacheData(CACHE_KEYS.KEYWORDS, keywordsRes.data.keywords),
        timestamp: cacheData(CACHE_KEYS.LAST_FETCH, Date.now())
      };
      
      // Log cache update results
      console.log('Cache update results:', {
        channels: cacheResults.channels ? 'success' : 'failed',
        messages: cacheResults.messages ? 'success' : 'failed',
        keywords: cacheResults.keywords ? 'success' : 'failed',
        timestamp: cacheResults.timestamp ? 'success' : 'failed',
        messageCount: messagesData.length
      });
      
      // Log cache usage for debugging
      logCacheUsage();
      
      setError(null);
    } catch (error) {
      console.error("Data fetching error:", error);
      setError("Failed to fetch data");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Periodic updates
  useEffect(() => {
    if (!edgeServerEndpoint) return;

    const updateInterval = setInterval(async () => {
      try {
        // Check cache health before fetching new data
        const cacheHealth = isCacheHealthy();
        if (!cacheHealth.healthy) {
          console.warn('Cache health check failed:', cacheHealth);
          // Clear cache if it's too large
          if (cacheHealth.totalSize > cacheHealth.maxSize) {
            console.log('Clearing cache due to size limit');
            Object.values(CACHE_KEYS).forEach(key => {
              localStorage.removeItem(key);
            });
          }
        }
        
        // Clear old cache before fetching new data
        clearOldCache();
        await fetchAllData();
      } catch (error) {
        console.error("Update error:", error);
      }
    }, 5000);

    return () => clearInterval(updateInterval);
  }, [edgeServerEndpoint]);

  if (loading && !Object.keys(channels).length) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error && !showModal) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-red-600">
          {error}
          <button 
            onClick={() => setShowModal(true)}
            className="ml-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Configure Endpoint
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md p-6 mx-4 bg-white rounded-lg shadow-xl">
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                {validationStatus === "failed" 
                  ? "Connection Failed" 
                  : "Enter Edge Server Endpoint"}
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                {validationStatus === "failed"
                  ? "Unable to connect to the server. Please check the URL and try again."
                  : "Please provide the URL for your Edge Server endpoint to continue."}
              </p>
              {error && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
              )}
            </div>

            <div className="mt-4">
              <input
                type="url"
                placeholder="https://your-server.com"
                value={endpointInput}
                onChange={(e) => setEndpointInput(e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  validationStatus === "failed" ? "border-red-500" : "border-gray-300"
                }`}
              />
            </div>

            <div className="mt-6">
              <button
                onClick={handleEndpointSubmit}
                disabled={validationStatus === "validating"}
                className="w-full px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:bg-blue-400"
              >
                {validationStatus === "validating" 
                  ? "Connecting..." 
                  : validationStatus === "failed"
                  ? "Try Again"
                  : "Connect to Server"}
              </button>
            </div>
          </div>
        </div>
      )}

      <AuthProvider>
 
     
        <Router>
          <Routes>
            <Route path="/login" element={
              <LoginPage 
                toggleTheme={toggleTheme} 
                isDarkMode={isDarkMode} 
                setIsDarkMode={setIsDarkMode}   
                edgeServerEndpoint={edgeServerEndpoint}  
              />
            } />
            <Route path="/" element={
              <PrivateRoute>
                <LiveCommunications
                  timezone={timezone}
                  timeFormat={timeFormat}
                  isDarkMode={isDarkMode}
                  edgeServerEndpoint={edgeServerEndpoint}
                  setMessages={setMessages}
                  setIsDarkMode={setIsDarkMode}
                  toggleTheme={toggleTheme}
                  channels={channels} 
                  messages={processedMessages}
                  keywords={keywords}
                  reverseSort={reverseSort}
                  setReverseSort={setReverseSort}
                />
              </PrivateRoute>
            } />
            <Route path="/settings" element={
              <PrivateRoute>
                <SettingsPage 
                  isDarkMode={isDarkMode} 
                  timezone={timezone} 
                  timeFormat={timeFormat}
                  setTimeFormat={setTimeFormatWithLogging}
                  reverseSort={reverseSort} 
                  setReverseSort={setReverseSort} 
                />
              </PrivateRoute>
            } />
            <Route path="/users" element={
              <PrivateRoute>
                <UserManagement isDarkMode={isDarkMode} />
              </PrivateRoute>
            } />

<Route path="/advanced-player" element={<AdvancedAudioPlayer isDarkMode={isDarkMode} timeFormat={timeFormat} />} />
            <Route path="/logs" element={
              <PrivateRoute>
                <LogsPage edgeServerEndpoint={edgeServerEndpoint} timezone={timezone} timeFormat={timeFormat} />
              </PrivateRoute>
            } />
            <Route path="/report" element={
              <PrivateRoute>
                <ReportPage isDarkMode={isDarkMode} timeFormat={timeFormat} />
              </PrivateRoute>
            } />
          </Routes>
        </Router>
        <ToastContainer 
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          limit={3}
          enableMultiContainer={false}
        />
      </AuthProvider>
    </>
  );
};
export default App;